/**
 * The shell's plugin API. A plugin module's default export is called once:
 *
 *   export default function review(api, options) {
 *     api.addPanel({ id: 'review', title: 'Review', mount(el, ctx) { …; return () => cleanup } });
 *     api.addBadge((entry) => ({ label: 'In review', tone: 'outline', signal: 'warning' }) | null);
 *       // tone: outline | solid | muted | dot | dot-outline
 *       // signal (optional): success | warning | danger, for state only
 *     api.addPageSection({ id, mount(el, ctx) })   // under a docs page's header strip
 *     api.on('select', ({ id, entry }) => {});
 *   }
 *
 * Panels and sections are plain DOM, so plugins carry no framework and never
 * share React with the shell.
 */
export function createPluginHost({ getState, navigate, refresh }) {
  const panels = [];
  const badges = [];
  const sections = [];
  const listeners = new Map();

  const api = {
    addPanel(panel) {
      panels.push(panel);
      refresh();
    },
    addBadge(fn) {
      badges.push(fn);
      refresh();
    },
    addPageSection(section) {
      sections.push(section);
      refresh();
    },
    on(event, fn) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event).add(fn);
      return () => listeners.get(event).delete(fn);
    },
    getState,
    navigate,
    refresh,
  };

  return {
    api,
    panels,
    sections,
    badgesFor(entry) {
      const out = [];
      for (const fn of badges) {
        try {
          const b = fn(entry);
          if (b) out.push(...(Array.isArray(b) ? b : [b]));
        } catch (error) {
          console.error('edl-docs: a badge plugin threw', error);
        }
      }
      return out;
    },
    emit(event, payload) {
      for (const fn of listeners.get(event) ?? []) {
        try {
          fn(payload);
        } catch (error) {
          console.error(`edl-docs: a plugin's ${event} listener threw`, error);
        }
      }
    },
    async load(modules = []) {
      for (const mod of modules) {
        try {
          const loaded = await import(/* @vite-ignore */ mod.url);
          await loaded.default?.(api, mod.options ?? {});
        } catch (error) {
          console.error(`edl-docs: plugin "${mod.name}" failed to load`, error);
        }
      }
    },
  };
}
